import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ButtonsContainer,
  TitleAndSubtitle,
  ContentButtonsWrapper,
} from '../../../layout/layout-container.styled';
import { NavButton } from '../../../nav-button/nav-button';
import { Text, font } from '../../../../style-utils/text';
import { InputContainer } from '../../../login/login.styled';
import { DefaultComponentProps } from '../../../app-router/component-props';
import { DailyLogState } from '../../../hooks/use-daily-log-handler';
import { palette, spacing } from '../../../../style-utils/theme';
import { PersonField } from '../../../capture/person-field';
import { ScanIdButton } from '../../../capture/scan-id-button';
import styled from '@emotion/styled';

const ScanRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.xs};
`;

// The daily log's name field refers to a RESIDENT — the subtitle below says so
// verbatim — so unlike the visitor log's guest names it resolves to a person.
//
// `resolves` is therefore true on the given name, which carries the picker, and
// false on the surname: one person, one link. Splitting the resolution across
// two inputs would let a concierge pick two different residents for one human.
export const NameAndLastName: React.FC<DefaultComponentProps> = ({
  navigate,
  changeHandler,
  state,
  nextPath,
  prevPath,
  setCapture,
}) => {
  const {
    name,
    middleName,
    lastName,
    property_ids,
    unit_id,
    subject_occupancy_id,
    subject_resolution,
    field_provenance,
  } = state as DailyLogState;

  const buildingId = property_ids?.[0] ?? null;

  // 2+ current residents match and nobody has chosen. Reported UP from
  // PersonField, which is the only place that knows the candidate count.
  //
  // The server refuses this save with a 400 and the database refuses it with a
  // CHECK, so blocking Continue here is the courteous version of a rejection
  // that is coming anyway — not the enforcement itself.
  const { pathname } = useLocation();
  const [mustPick, setMustPick] = useState(false);

  /**
   * WHOSE NAME THIS IS DEPENDS ON THE FLOW THAT REACHED IT.
   *
   * This component is registered by BOTH `/daily-log/resident/id` and
   * `/daily-log/shift-change/id`, and its subtitle was the hardcoded string
   * "Name and Last name for the resident". So the Shift Change flow — where the
   * name being recorded is the COLLEAGUE taking or handing over the desk — asked
   * the concierge for "the resident", which is the wrong person entirely and the
   * kind of prompt that gets a wrong name written into a log.
   */
  const subtitle = pathname.includes('/shift-change/')
    ? 'Name and last name of the person taking over the desk'
    : 'Name and last name for the resident';
  const handleRequirePick = useCallback((v: boolean) => setMustPick(v), []);

  return (
    <>
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          {`ID (Name And Last Name)`}
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          {subtitle}
        </Text>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        {/* gap: the two names are separate questions, and each carries a label,
            a provenance badge and (on the given name) a scan button. */}
        <InputContainer
          style={{ gap: spacing.md, display: 'flex', flexDirection: 'column' }}
        >
          <PersonField
            label="Name"
            value={name}
            onChange={next => changeHandler(next, 'name')}
            resolves
            buildingId={buildingId}
            unitId={unit_id}
            resolution={subject_resolution}
            occupancyId={subject_occupancy_id}
            onResolve={picked =>
              setCapture?.({
                subject_occupancy_id: picked.occupancy_id,
                subject_resolution: picked.resolution,
              })
            }
            source={field_provenance?.name?.source}
            scanRaw={field_provenance?.name?.raw}
            onRequirePickChange={handleRequirePick}
            maxLength={30}
            placeholder="Enter name"
          />
          {/* Optional by design: most people do not have one, and an empty
              middle name is the honest value rather than a gap to be filled.
              `resolves` is false — the resident is resolved once, on the given
              name above. */}
          <PersonField
            label="Middle Name (optional)"
            value={middleName || ''}
            onChange={next => changeHandler(next, 'middleName')}
            resolves={false}
            source={field_provenance?.middleName?.source}
            scanRaw={field_provenance?.middleName?.raw}
            maxLength={50}
            placeholder="Enter middle name"
          />
          <PersonField
            label="Last Name"
            value={lastName}
            onChange={next => changeHandler(next, 'lastName')}
            // No second picker: the resident is resolved once, on the given
            // name above.
            resolves={false}
            source={field_provenance?.lastName?.source}
            scanRaw={field_provenance?.lastName?.raw}
            maxLength={50}
            placeholder="Enter last name"
          />

          {/* CENTRED UNDER BOTH FIELDS. One scan fills both halves of the
              name, so the button belongs to the pair — hung off the given-name
              field it looked like it only applied to that one, and left the
              surname looking like it had no scan at all. */}
          <ScanRow>
            <ScanIdButton
              buildingId={buildingId}
              label="Scan ID to fill both"
              onResult={r => {
                changeHandler(r.first_name, 'name');
                changeHandler(r.middle || '', 'middleName');
                changeHandler(r.last_name, 'lastName');
                setCapture?.({
                  // Clear any earlier resolution: the name just changed, so who
                  // it refers to is an open question again and autocapture
                  // should re-run against the new value.
                  subject_occupancy_id: null,
                  subject_resolution: 'unset',
                  field_provenance: {
                    ...field_provenance,
                    // `raw` is what the SCANNER read. Sent as plaintext and
                    // encrypted server-side; it is what a later edit is
                    // compared against to promote 'scan' to 'scan_edited'.
                    name: {
                      source: 'scan',
                      raw: r.raw.first_name,
                      confidence: r.confidence,
                    },
                    middleName: {
                      source: 'scan',
                      raw: r.raw.middle_name || '',
                      confidence: r.confidence,
                    },
                    lastName: {
                      source: 'scan',
                      raw: r.raw.last_name,
                      confidence: r.confidence,
                    },
                  },
                });
              }}
            />
          </ScanRow>
        </InputContainer>
      </ContentButtonsWrapper>
      <ButtonsContainer>
        <NavButton
          disabled={false}
          direction="back"
          textColor={palette.slate}
          background="transparent"
          text={'Back'}
          onClick={() => navigate(prevPath, { replace: false })}
        />
        <NavButton
          disabled={
            lastName.length < 1 || name.length < 1 || mustPick ? true : false
          }
          direction="forward"
          text={'Continue'}
          onClick={() => navigate(nextPath, { replace: false })}
        />
      </ButtonsContainer>
    </>
  );
};
